// client/src/lib/logger.ts
//
// Logger centralizado de GrindLog.
// - Siempre escribe en consola (debug)
// - Persiste errors/warns en Supabase error_logs (fire-and-forget)
// - Nunca lanza excepciones propias — si falla el log, falla silenciosamente

import { supabase, SUPABASE_ENABLED } from './supabase';

type LogLevel = 'error' | 'warn';

export interface LogContext {
  [key: string]: unknown;
}

// userId se setea desde useAuth cuando el estado de auth cambia
let _userId: string | null = null;

export function setLoggerUser(userId: string | null): void {
  _userId = userId;
}

// ─── Persistencia en Supabase (fire-and-forget) ───────────────────────────────

function persist(
  level: LogLevel,
  source: string,
  message: string,
  stack?: string,
  context?: LogContext,
): void {
  if (!SUPABASE_ENABLED || !supabase) return;

  // No esperamos el resultado — el logger no bloquea el flujo del usuario
  supabase
    .from('error_logs')
    .insert({
      user_id: _userId,
      level,
      source,
      message: message.slice(0, 500),
      stack: stack ? stack.slice(0, 2000) : null,
      context: context ?? null,
    })
    .then((res: { error: { message: string } | null }) => {
      // Si falla el INSERT del log, solo lo imprimimos en consola (nunca lanzamos)
      if (res.error) {
        console.warn('[logger] No se pudo persistir el log:', res.error.message);
      }
    })
    .catch(() => {
      // Silencio total si Supabase no responde
    });
}

// ─── API pública ──────────────────────────────────────────────────────────────

export const logger = {
  /**
   * Registra un error inesperado.
   * @param source  Nombre del módulo/componente (e.g. 'ImportView', 'useDB')
   * @param message Descripción legible del error
   * @param err     El objeto Error original (opcional)
   * @param context Datos extra estructurados (fileName, tournamentId, etc.)
   */
  error(source: string, message: string, err?: unknown, context?: LogContext): void {
    const errorMsg = err instanceof Error ? err.message : String(err ?? '');
    const stack    = err instanceof Error ? err.stack : undefined;
    const full     = errorMsg ? `${message}: ${errorMsg}` : message;

    console.error(`[${source}]`, full, err ?? '');
    persist('error', source, full, stack, context);
  },

  /**
   * Registra una advertencia — situación anómala pero no fatal.
   */
  warn(source: string, message: string, context?: LogContext): void {
    console.warn(`[${source}]`, message, context ?? '');
    persist('warn', source, message, undefined, context);
  },

  /**
   * Log informativo — solo consola, no persiste en Supabase.
   */
  info(source: string, message: string): void {
    console.log(`[${source}]`, message);
  },
};
